// Book a lesson — the client's side of the schedule.
//
// Taylor: "The client is able to go in and select the best schedule slots" and
// "Client can see available lesson slots, training rides, if other clients are
// scheduled."
//
// Every slot in the window is shown, full ones included. A client deciding
// which Thursday to take needs to see that Thursday at 4 is full and who's in
// it — hiding it just makes the calendar look emptier than the barn is.
//
// The server decides what is bookable; this page only renders the answer.
// `bookable` already accounts for the cutoff, capacity, whether booking is on
// at all, and whether you're in the slot already, so there is no second copy of
// those rules here to drift out of sync.
//
// No horse picker on purpose. The trainer assigns horses.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { colorClass } from '../../lib/lessonTypes';
import { fmtDate } from '../../lib/dates';

const HORIZON_DAYS = 14;

export default function BookPage() {
  const qc = useQueryClient();
  const [riderId, setRiderId] = useState('');
  const [err, setErr] = useState('');
  const [pending, setPending] = useState(null); // "date startTime" of the slot in flight

  const slotsQ = useQuery({
    queryKey: ['slots'],
    queryFn: () => api.get('/slots'),
  });

  const data = slotsQ.data;
  const riders = data?.riders ?? [];
  // One rider is the common case, so don't make them choose.
  const activeRider = riderId || (riders.length === 1 ? riders[0].id : '');

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['slots'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
    qc.invalidateQueries({ queryKey: ['schedule'] });
  };

  const book = useMutation({
    mutationFn: (slot) =>
      api.post('/slots/book', {
        availabilityId: slot.availabilityId,
        date: slot.date,
        startTime: slot.startTime,
        riderPersonId: activeRider,
      }),
    onSuccess: () => { setErr(''); setPending(null); refresh(); },
    onError: (e) => { setErr(friendly(e)); setPending(null); refresh(); },
  });

  const cancel = useMutation({
    mutationFn: (lessonId) => api.del(`/slots/booking/${lessonId}`),
    onSuccess: () => { setErr(''); setPending(null); refresh(); },
    onError: (e) => { setErr(friendly(e)); setPending(null); refresh(); },
  });

  const act = (slot) => {
    setPending(`${slot.date} ${slot.startTime}`);
    if (slot.iBooked && slot.myLessonId) cancel.mutate(slot.myLessonId);
    else book.mutate(slot);
  };

  const days = groupByDay(data?.slots ?? []);

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4 md:space-y-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-semibold">Book a lesson</h1>
        <p className="text-ink-400 text-sm mt-1">
          The next {HORIZON_DAYS} days
          {data?.cutoffHours > 0 && ` · changes close ${hoursLabel(data.cutoffHours)} ahead`}
        </p>
      </header>

      {err && <div className="rounded-lg bg-rose-50 text-rose-700 text-sm px-3 py-2">{err}</div>}

      {data && !data.bookingEnabled && (
        <div className="rounded-lg bg-amber-50 text-amber-800 text-sm px-3 py-2">
          Online booking is off for this barn. You can see what's available — talk to your
          trainer to get on the schedule.
        </div>
      )}

      {data && data.bookingEnabled && riders.length === 0 && (
        <div className="rounded-lg bg-amber-50 text-amber-800 text-sm px-3 py-2">
          Your account isn't linked to a rider yet, so there's nobody to book for. Ask your
          barn to link you.
        </div>
      )}

      {riders.length > 1 && (
        <div className="card p-4">
          <label className="block text-sm font-medium mb-1" htmlFor="rider">
            Booking for
          </label>
          <select
            id="rider"
            className="input w-full sm:w-64"
            value={activeRider}
            onChange={(e) => setRiderId(e.target.value)}
          >
            <option value="">Choose a rider…</option>
            {riders.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>
      )}

      {slotsQ.isLoading ? (
        <Skeleton />
      ) : days.length === 0 ? (
        <div className="card p-6 text-center">
          <p className="text-ink-500">No lesson times are published for the next two weeks.</p>
          <p className="text-sm text-ink-400 mt-1">
            Your trainer sets these under Availability.
          </p>
        </div>
      ) : (
        days.map(([date, slots]) => (
          <div key={date} className="card p-4">
            <h2 className="font-medium mb-3">{dayLabel(date, data?.today)}</h2>
            <ul className="space-y-2">
              {slots.map((s) => (
                <SlotRow
                  key={`${s.availabilityId}-${s.startTime}`}
                  slot={s}
                  canAct={!!activeRider || s.iBooked}
                  busy={pending === `${s.date} ${s.startTime}`}
                  onAct={() => act(s)}
                />
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function SlotRow({ slot, canAct, busy, onAct }) {
  const full = slot.open <= 0 && !slot.iBooked;
  const others = slot.riders.filter((r) => r.lessonId !== slot.myLessonId);

  return (
    <li
      className={
        'flex items-center gap-3 rounded-lg border px-3 py-2 ' +
        (slot.iBooked
          ? 'border-emerald-300 bg-emerald-50'
          : full
            ? 'border-ink-100 bg-ink-50'
            : 'border-ink-100')
      }
    >
      <span className={'w-1 self-stretch rounded-full shrink-0 ' + colorClass(slot.lessonTypeColor, 'spine')} />

      <span className="min-w-0 flex-1">
        <span className="block font-medium">
          {formatTime(slot.startTime)}
          <span className="text-ink-400 font-normal"> – {formatTime(slot.endTime)}</span>
        </span>
        <span className="block text-sm text-ink-500 truncate">
          {[slot.lessonTypeName, slot.trainerName].filter(Boolean).join(' · ')}
        </span>
        {/* Who else is in it — Taylor asked for this explicitly. */}
        {others.length > 0 && (
          <span className="block text-xs text-ink-400 truncate mt-0.5">
            With {others.map((r) => r.name).join(', ')}
          </span>
        )}
        {/* Said before the button is pressed, not after. A slot that has to be
            asked for is a different offer from one that can be taken. */}
        {slot.needsApproval && !slot.iBooked && (
          <span className="block text-xs text-amber-700 mt-0.5">
            Needs the trainer&apos;s OK
          </span>
        )}
      </span>

      <span className="shrink-0 text-right">
        {slot.capacity > 1 && (
          <span className="block text-xs text-ink-400 mb-1">
            {slot.booked} of {slot.capacity} booked
          </span>
        )}
        {slot.iBooked ? (
          <button
            type="button"
            className="btn-secondary text-sm"
            disabled={busy || !slot.myLessonId}
            onClick={onAct}
          >
            {busy ? 'Canceling…' : 'Cancel'}
          </button>
        ) : slot.bookable && canAct ? (
          // "Request" where the trainer has to agree. Pressing a button marked
          // Book and getting something that is not booked is the kind of
          // surprise people ring the barn about.
          <button type="button" className="btn-primary text-sm" disabled={busy} onClick={onAct}>
            {busy
              ? slot.needsApproval ? 'Asking…' : 'Booking…'
              : slot.needsApproval ? 'Request' : 'Book'}
          </button>
        ) : (
          <span className="text-xs text-ink-400">{full ? 'Full' : 'Closed'}</span>
        )}
      </span>
    </li>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4">
      {[0, 1].map((i) => (
        <div key={i} className="card p-4 space-y-2">
          <div className="h-4 w-32 bg-ink-100 rounded animate-pulse" />
          <div className="h-10 bg-ink-100 rounded animate-pulse" />
          <div className="h-10 bg-ink-100 rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}

/** Keep the server's ordering; just break it into days. */
function groupByDay(slots) {
  const days = new Map();
  for (const s of slots) {
    const list = days.get(s.date);
    if (list) list.push(s);
    else days.set(s.date, [s]);
  }
  return [...days.entries()];
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Parsed as UTC so the label can't drift a day against the barn-local date.
function dayLabel(iso, today) {
  const d = new Date(iso + 'T00:00:00Z');
  const name = iso === today ? 'Today' : DAY_NAMES[d.getUTCDay()];
  return `${name}, ${fmtDate(iso, 'short')}`;
}

function formatTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function hoursLabel(hours) {
  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? 'a day' : `${days} days`;
  }
  return hours === 1 ? 'an hour' : `${hours} hours`;
}

function friendly(e) {
  switch (e?.body?.error || e?.message || '') {
    case 'slot_full':
      return 'Someone took that spot first. Pick another time.';
    case 'rider_already_booked':
      return 'That rider already has a lesson at that time.';
    case 'slot_not_available':
      return 'That time is no longer offered. The schedule has moved on — try another.';
    case 'past_cutoff':
      return 'That one is too close to start to change online. Call the barn.';
    case 'booking_disabled':
      return 'Online booking is off for this barn.';
    case 'not_your_rider':
      return 'You can only book for riders linked to your account.';
    case 'not_your_booking':
      return 'That booking belongs to someone else.';
    default:
      return 'That did not go through. Try again.';
  }
}
